<script setup>
import { onMounted, ref } from "vue";
import SelectItem from "../../SelectItem.vue";
import DepositFiat from "./DepositFiat.vue";
import DepositDummy from "./DepositDummy.vue";
import DepositCrypto from "./DepositCrypto.vue";
import WithdrawFlutterwave from "./WithdrawFlutterwave.vue";
import WithdrawDummy from "./WithdrawDummy.vue";
import WithdrawCrypto from "./WithdrawCrypto.vue";
import { t } from "@/app/composables/useI18n";
import { useUserService } from "@/user-account/composables/useUserService";
import { api } from "@/services";
import { useCurrencies } from "@/app/composables/useCurrencies";
import ClipLoader from "vue-spinner/src/ClipLoader.vue";
const props = defineProps({
  type: {
    type: String,
    required: true,
  },
});

const { fundWallets, activeWallet } = useUserService();
const { getIconByTicker } = useCurrencies();
const allowedCurrencies = ref([]);
const providers = ref([]);
const loading = ref(false);
const selectedCurrency = ref({});
const selectedProvider = ref({});

const actionNames = {
  deposit: "deposit",
  withdraw: "withdrawal",
};

async function loadAllowedCurrencies() {
  allowedCurrencies.value = [];

  loading.value = true;
  const result = await api.finance.allowedCurrencies(actionNames[props.type]);
  allowedCurrencies.value = result.currencies;
  loading.value = false;
  selectedCurrency.value = activeWallet.value.currency;

  if (allowedCurrencies.value.length > 0) {
    loadProviders(allowedCurrencies.value[0].id);
  }
}

async function loadProviders(currencyId) {
  providers.value = [];

  loading.value = true;
  providers.value = await api.finance.providers(currencyId, actionNames[props.type]);
  loading.value = false;
  selectedProvider.value = providers.value[0];
}

function selectCurrency(currency) {
  selectedCurrency.value = currency;

  loadProviders(currency.id);
}

onMounted(() => {
  loadAllowedCurrencies();
});
</script>

<template>
  <div v-if="allowedCurrencies.length > 0">
    <div class="label">
      {{ t("finance.select_currency") }}
    </div>
    <div class="select-item">
      <SelectItem :value="selectedCurrency.name" :icon="selectedCurrency?.ticker">
        <template #header>
          <div
            v-for="currency in fundWallets"
            @click="selectCurrency(currency?.currency)"
            :key="currency?.currency?.id"
          >
            <div class="currency-balance">
              <img
                :src="getIconByTicker(currency?.currency?.ticker)"
                class="currency-icon"
                alt=""
              />
              {{ currency?.currency?.name }}
            </div>
          </div>
        </template>
      </SelectItem>
    </div>

    <div v-if="providers.length > 1" class="label">
      {{ t("finance.select_billing") }}
    </div>
    <div v-if="providers.length > 1" class="select-item">
      <SelectItem :value="selectedProvider.name">
        <template #header>
          <div
            v-for="provider in providers"
            @click="selectedProvider = provider"
            :key="provider.id"
          >
            {{ provider.name }}
          </div>
        </template>
      </SelectItem>
    </div>

    <DepositFiat
      v-if="
        props.type === 'deposit' && ['flutterwave', 'sentinal'].includes(selectedProvider?.alias)
      "
      :currency-id="parseInt(selectedCurrency.id)"
      :provider-id="parseInt(selectedProvider.id)"
    />
    <DepositCrypto
      v-else-if="props.type === 'deposit' && ['coins-paid'].includes(selectedProvider?.alias)"
      :currency-id="parseInt(selectedCurrency.id)"
      :provider-id="parseInt(selectedProvider.id)"
    />
    <DepositDummy
      v-else-if="props.type === 'deposit' && ['dummy'].includes(selectedProvider?.alias)"
      :currency-id="parseInt(selectedCurrency.id)"
      :provider-id="parseInt(selectedProvider.id)"
    />
    <WithdrawFlutterwave
      v-else-if="props.type === 'withdraw' && ['flutterwave'].includes(selectedProvider?.alias)"
      :currency-id="parseInt(selectedCurrency.id)"
      :provider-id="parseInt(selectedProvider.id)"
    />
    <WithdrawCrypto
      v-else-if="props.type === 'withdraw' && ['coins-paid'].includes(selectedProvider?.alias)"
      :currency-id="parseInt(selectedCurrency.id)"
      :provider-id="parseInt(selectedProvider.id)"
    />
    <WithdrawDummy
      v-else-if="props.type === 'withdraw' && ['dummy'].includes(selectedProvider?.alias)"
      :currency-id="parseInt(selectedCurrency.id)"
      :provider-id="parseInt(selectedProvider.id)"
    />
  </div>

  <ClipLoader v-if="loading" :loading="true" color="white" size="20px" />

  <div v-if="providers.length === 0 && !loading">
    <span v-if="props.type === 'deposit'">{{ t("finance.deposit_unsupported") }}</span>
    <span v-else>{{ t("finance.withdraw_unsupported") }}</span>
  </div>

  <div v-if="allowedCurrencies.length === 0 && !loading">
    <span v-if="props.type === 'deposit'">{{ t("finance.deposit-not-allowed") }}</span>
    <span v-else>{{ t("finance.withdrawals_not_posible_message") }}</span>
  </div>
</template>

<style lang="scss" scoped>
.btn-g {
  color: var(--be-primary-btn-label);
  border: none;
  height: 42px;
  width: 100%;
  border-radius: 4px;
  background: var(--Linear, linear-gradient(90deg, #3886ec 0%, #544fe5 100%));
  margin: 12px 0px;
}

.currency-balance {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
}

.currency-icon {
  width: 16px;
  height: 21px;
}

.withdraw-fee {
  font-size: 12px;
}

.label {
  margin-bottom: 7px;
}

.select-item {
  margin-bottom: 10px;
  height: 40px;
}

.deposit-amount {
  font-size: 14px;
  border-radius: 4px;
  background: var(--be-small-card-bg);
  border: none;
  height: 40px;
  outline: none;
  width: 100%;
  color: white;
  text-indent: 10px;
}

.min-deposit {
  display: flex;
  justify-content: space-between;
  margin-top: 15px;
}

.qr-code {
  display: flex;
  padding: 16px;
  border-radius: 4px;
  background: var(--be-small-card-bg);
  gap: 0 16px;
}
</style>
